import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Calendar, User, CheckCircle2, Circle, AlertCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import TaskDialog from "./TaskDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TasksPage = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [allTasksCount, setAllTasksCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, pending, completed
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  useEffect(() => {
    fetchTasks();
  }, [filter]);

  const fetchTasks = async () => {
    try {
      // Always fetch counts for all statuses
      const allTasksResponse = await axios.get(`${API}/tasks`, {
        withCredentials: true
      });
      const allTasks = allTasksResponse.data;
      
      setAllTasksCount(allTasks.length);
      setPendingCount(allTasks.filter(t => t.status === "pending").length);
      setCompletedCount(allTasks.filter(t => t.status === "completed").length);
      
      // If filter is "all", use all tasks, otherwise filter
      if (filter === "all") {
        setTasks(allTasks);
      } else {
        setTasks(allTasks.filter(t => t.status === filter));
      }
      
      setLoading(false);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast.error("Failed to fetch tasks");
      setLoading(false);
    }
  };

  const toggleTaskStatus = async (task) => {
    const newStatus = task.status === "completed" ? "pending" : "completed";
    await updateTaskStatus(task.id, newStatus);
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      await axios.patch(`${API}/tasks/${taskId}`, {
        status: newStatus
      }, {
        withCredentials: true
      });
      toast.success(`Task marked as ${newStatus}`);
      fetchTasks();
    } catch (error) {
      toast.error("Failed to update task status");
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;
    
    try {
      await axios.delete(`${API}/tasks/${taskId}`, {
        withCredentials: true
      });
      toast.success("Task deleted");
      fetchTasks();
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high": return "destructive";
      case "medium": return "default";
      case "low": return "secondary";
      default: return "default";
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case "high": return <AlertCircle className="w-4 h-4" />;
      case "medium": return <Circle className="w-4 h-4" />;
      case "low": return <Circle className="w-4 h-4" />;
      default: return null;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No due date";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const isOverdue = (dateString) => {
    if (!dateString) return false;
    return new Date(dateString) < new Date() && tasks.find(t => t.due_date === dateString)?.status !== "completed";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-slate-500">Loading tasks...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Tasks & Reminders</h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">Manage your tasks and stay on top of follow-ups</p>
        </div>
        <Button onClick={() => {
          setEditingTask(null);
          setShowTaskDialog(true);
        }}>
          <Plus className="w-4 h-4 mr-2" />
          Add Task
        </Button>
      </div>

      {/* Filters */}
      <div className="flex space-x-2">
        <Button
          variant={filter === "all" ? "default" : "outline"}
          onClick={() => setFilter("all")}
        >
          All Tasks ({allTasksCount})
        </Button>
        <Button
          variant={filter === "pending" ? "default" : "outline"}
          onClick={() => setFilter("pending")}
        >
          Pending ({pendingCount})
        </Button>
        <Button
          variant={filter === "completed" ? "default" : "outline"}
          onClick={() => setFilter("completed")}
        >
          Completed ({completedCount})
        </Button>
      </div>

      {/* Tasks List */}
      <div className="space-y-3">
        {tasks.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-slate-500">No tasks found. Create your first task to get started!</p>
            </CardContent>
          </Card>
        ) : (
          tasks.map((task) => (
            <Card key={task.id} className={task.status === "completed" ? "opacity-60" : ""}>
              <CardContent className="py-4">
                <div className="flex items-start space-x-4">
                  {/* Checkbox */}
                  <Checkbox
                    checked={task.status === "completed"}
                    onCheckedChange={() => toggleTaskStatus(task)}
                    className="mt-1"
                  />

                  {/* Task Details */}
                  <div className="flex-1 space-y-2">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <h3 className={`font-semibold text-lg ${task.status === "completed" ? "line-through text-slate-500" : "text-slate-900"}`}>
                          {task.title}
                        </h3>
                        {task.description && (
                          <p className="text-sm text-slate-600">{task.description}</p>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingTask(task);
                            setShowTaskDialog(true);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteTask(task.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>

                    {/* Meta info */}
                    <div className="flex items-center space-x-4 text-sm text-slate-500">
                      {task.due_date && (
                        <div className={`flex items-center space-x-1 ${isOverdue(task.due_date) ? "text-red-600 font-medium" : ""}`}>
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(task.due_date)}</span>
                          {isOverdue(task.due_date) && <span className="text-red-600">(Overdue)</span>}
                        </div>
                      )}
                      <Badge variant={getPriorityColor(task.priority)} className="flex items-center space-x-1">
                        {getPriorityIcon(task.priority)}
                        <span>{task.priority}</span>
                      </Badge>
                      
                      {/* Quick Status Change Dropdown */}
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-slate-500">Status:</span>
                        <Select 
                          value={task.status} 
                          onValueChange={(newStatus) => updateTaskStatus(task.id, newStatus)}
                        >
                          <SelectTrigger className="h-7 w-[120px] text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">
                              <span className="flex items-center space-x-2">
                                <Circle className="w-3 h-3 text-yellow-500" />
                                <span>Pending</span>
                              </span>
                            </SelectItem>
                            <SelectItem value="completed">
                              <span className="flex items-center space-x-2">
                                <CheckCircle2 className="w-3 h-3 text-green-500" />
                                <span>Completed</span>
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Task Dialog */}
      {showTaskDialog && (
        <TaskDialog
          open={showTaskDialog}
          onClose={() => {
            setShowTaskDialog(false);
            setEditingTask(null);
          }}
          onSuccess={fetchTasks}
          task={editingTask}
        />
      )}
    </div>
  );
};

export default TasksPage;
